import { defineStore, getActivePinia } from 'pinia'
import type { AuthState, LoginCredentials, LoginResponse } from '@/types/auth'
import { authApi } from '@/api/auth'

function triggerPreload() {
  // Lazy imports to avoid circular dependency (generated-client → auth → portfolio → generated-client)
  import('@/stores/portfolio').then(({ usePortfolioStore }) => {
    usePortfolioStore().loadAll()
  })
  import('@/stores/broker').then(({ useBrokerStore }) => {
    useBrokerStore().loadAll()
  })
  import('@/stores/product').then(({ useProductStore }) => {
    useProductStore().loadAll()
  })
}

function resetPreloadStores() {
  // Reset stores synchronously through Pinia's internal store registry
  const pinia = getActivePinia()
  if (!pinia) return

  const stores = (pinia as unknown as { _s: Map<string, { reset?: () => void; $reset?: () => void }> })._s
  for (const id of ['portfolio', 'broker', 'product']) {
    const store = stores.get(id)
    if (store?.reset) {
      store.reset()
    }
  }
}

export const useAuthStore = defineStore('auth', {
  state: (): AuthState => ({
    user: null,
    accessToken: null,
  }),

  getters: {
    isAuthenticated: (state) => !!state.accessToken && !!state.user,
    hasRole: (state) => (role: string) => state.user?.role === role,
    platformRoles: (state) => state.user?.platformRoles ?? [],
    // Backend returns snake_case roles: 'site_admin', 'site_owner', 'main_reseller', 'sub_reseller', 'salesman', 'customer'
    isSiteAdmin: (state) => state.user?.role === 'site_admin',
    isSiteOwner: (state) => state.user?.role === 'site_owner',
    isAdmin: (state) => state.user?.role === 'site_admin' || state.user?.role === 'site_owner',
    isReseller: (state) => state.user?.role === 'main_reseller' || state.user?.role === 'sub_reseller',
    isSalesman: (state) => state.user?.role === 'salesman',
    // For backward compatibility with components expecting roles array
    userRoles: (state) => state.user?.role ? [state.user.role] : [],
  },

  actions: {
    async login(credentials: LoginCredentials) {
      const response = await authApi.login(credentials)
      this.setAuthData(response)
      triggerPreload()
      return response
    },

    async logout() {
      try {
        await authApi.logout()
      } catch {
        // Ignore logout errors
      } finally {
        this.clearAuthData()
        resetPreloadStores()
      }
    },

    async fetchCurrentUser() {
      const user = await authApi.me()
      this.user = user
      return user
    },

    setAuthData(data: LoginResponse) {
      // 將後端 response 轉換為 store 格式 (interceptor 已將 snake_case 轉為 camelCase)
      this.accessToken = data.accessToken
      this.user = {
        id: data.userId,
        email: data.email,
        name: data.name,
        role: data.role,
        platformRoles: data.platformRoles ?? [],
      }

      // Persist to localStorage
      localStorage.setItem('accessToken', data.accessToken)
      localStorage.setItem('user', JSON.stringify(this.user))
    },

    clearAuthData() {
      this.user = null
      this.accessToken = null

      localStorage.removeItem('accessToken')
      localStorage.removeItem('user')
    },

    initFromStorage() {
      const accessToken = localStorage.getItem('accessToken')
      const userStr = localStorage.getItem('user')

      if (accessToken && userStr) {
        this.accessToken = accessToken
        try {
          this.user = JSON.parse(userStr)
          // 驗證 token 是否有效，成功後觸發預載
          this.fetchCurrentUser()
            .then(() => triggerPreload())
            .catch(() => this.clearAuthData())
        } catch {
          this.clearAuthData()
        }
      }
    },
  },
})
